import Vue from 'vue'
import store from '@/store/index'
import { TARIFF } from '@/utils/constants/drs/tariffs'
import cloneDumb from '@/helpers/cloneDumb'
import router from '@/router'

export const CONFIG_MODES = {
    CREATE: 'create',
    EDIT: 'edit',
    DEALER_EDIT: 'dealerEdit',
    MAPPING: 'mapping'
}

const state = () => ({
    selectedTariff: null,
    selectedShops: [],
    tariffs: [],
    devices: [],
    configMode: CONFIG_MODES.CREATE,
    orders: [],
    invoiceEmail: '',
    selectedPaymentType: 'automatic',
    tariffsCopy: null,
    cart: null,
    isCartLoading: false
})

const mutations = {
    SELECT_TARIFF(state, tariff) {
        state.selectedTariff = tariff
    },

    UPDATE_TARIFFS(state, tariff) {
        state.selectedTariff = tariff
    },

    RESET_DATA(state) {
        state.selectedTariff = null
        state.selectedShops = []
        state.cart = null
        state.isCartLoading = false
    },

    SET_TARIFFS(state, tariffs) {
        state.tariffs = tariffs
    },

    SET_DEVICES(state, devices) {
        state.devices = devices
    },

    SET_CONFIG_MODE(state, configMode) {
        state.configMode = configMode
    },

    SET_ORDERS(state, orders) {
        state.orders = orders
    },

    SET_INVOICE_EMAIL(state, email) {
        state.invoiceEmail = email
    },

    SET_SELECTED_PAYMENT_TYPE(state, paymentType) {
        state.selectedPaymentType = paymentType
    },
    SET_TARIFFS_COPY(state, tariffs) {
        state.tariffsCopy = tariffs
    },

    SET_CART(state, cart) {
        state.cart = cart
    },

    SET_IS_CART_LOADING(state, isLoading) {
        state.isCartLoading = isLoading
    }
}

const actions = {
    selectTariff({ commit, getters }, tariff) {
        if (tariff.available === false) {
            return
        }

        const activeDrsShops = getters.availableShops(null)
        const locations = tariff.locations || activeDrsShops.map(loc => ({
            ...loc,
            selected: true,
            additionalDevices: null,
            optionals: tariff.optionals?.list?.map(el => ({
                id: el.id,
                code: el.code,
                price: el.price,
                qty: 0
            }))
        }))
        Vue.set(tariff, 'locations', locations)

        commit('SELECT_TARIFF', tariff)
        commit('SET_CART', null)
    },

    selectEditableTariff({ commit, getters }, { subscription, selectedShopId, additionalDevices }) {
        const selectedLocation = getters.availableShops(selectedShopId)[0]
        const tariff = getters.tariffs.find(t => t.id === subscription.tariff_id)
        const subscriptionDevices = subscription.device_type_subscriptions || []
        const hasAdditionalDevicesBasedOnLimits = tariff.device_types.some(tariffDevice => {
            if (tariffDevice.code === 'md') {
                return false
            }

            const subscriptionDevice = subscriptionDevices.find(device => {
                return device.device_type === tariffDevice.code
            })
            const devicesAmount = Number(subscriptionDevice?.amount || 0)
            const freeDevicesLimit = Number(tariffDevice.free_device_limit || 0)

            return devicesAmount > freeDevicesLimit
        })
        const initialAdditionalDevices = additionalDevices || hasAdditionalDevicesBasedOnLimits

        const locations = [{
            ...selectedLocation,
            selected: true,
            additionalDevices: initialAdditionalDevices,
            optionals: tariff.device_types.map(el => ({
                ...el,
                qty: 0
            }))
        }]

        const selectedTariff = {
            ...tariff,
            locations,
            discounted_price: 0,
            price: 0
        }

        commit('UPDATE_TARIFFS', selectedTariff)
    },

    async loadDrsTariffs({ commit }, params = {}) {
        try {
            const { data, status } = await store.$api.get('/drs/api/proxy/zor/tariffs', {
                params: {
                    service_code: 'KEC',
                    ...params
                }
            })

            if (status !== 200 || !data?.tariffs?.length) {
                throw new Error('Failed to load DRS shops')
            }

            await commit('SET_TARIFFS', data.tariffs)
        } catch (error) {
            console.error('Error loading DRS tariffs:', error)
        }
    },

    async loadDevices({ commit, rootGetters }) {
        try {
            const { data, status } = await this.$api.get('drs/api/proxy/users/[drs_user_id]/devices')

            if (status !== 200 || !data?.devices?.length) {
                throw new Error('Failed to load DRS devices')
            }

            commit('SET_DEVICES', data.devices)
        } catch (error) {
            console.error('Error loading DRS devices:', error)
        }
    },

    async loadCart({ commit, getters, rootGetters }) {
        if (getters.configMode === CONFIG_MODES.EDIT) {
            const selectedTariff = getters.selectedTariff
            const tariffCopy = getters.tariffsCopy
            const selectedLocations = selectedTariff?.locations || []
            const tariffCopyLocations = tariffCopy?.locations || []
            let hasChanges = !!selectedTariff && !!tariffCopy && selectedTariff.id !== tariffCopy.id

            if (!hasChanges && selectedLocations.length > 0 && tariffCopyLocations.length > 0) {
                const additionalDevicesByLocationId = new Map(
                    tariffCopyLocations.map(location => [location.id, location.additionalDevices])
                )

                hasChanges = selectedLocations.some(location => {
                    return location.additionalDevices !== additionalDevicesByLocationId.get(location.id)
                })
            }

            if (!hasChanges) {
                commit('SET_CART', null)
                commit('SET_IS_CART_LOADING', false)
                return
            }
        }

        const payload = {
            nip: rootGetters['drs/nip'],
            locations: getters.cartLocations
        }

        if (!payload.nip || !payload.locations.length) {
            commit('SET_CART', null)
            commit('SET_IS_CART_LOADING', false)
            return
        }

        commit('SET_CART', null)
        commit('SET_IS_CART_LOADING', true)

        try {
            const { data } = await store.$api.post(
                'drs/api/proxy/zor/subscriptions/cart',
                payload,
                { params: { service_code: 'KEC' } }
            )

            commit('SET_CART', data)
        } catch (error) {
            if (store.$api.isCancel(error)) {
                return
            }

            console.error('Error calculating DRS cart:', error)
        }

        commit('SET_IS_CART_LOADING', false)
    },

    async createOrder({ rootGetters }, shop) {
        const user = rootGetters.getUser
        const company = rootGetters['drs/companiesData'][0]

        const orderPromises = shop.deviceTypes.filter(device => device.type !== null).map(device => {
            const payload = {
                deviceType: device.type,
                newDeviceCount: 0,
                upgradeDeviceCount: device.qty,
                orderAcquisitionType: 'DRS',
                orderSource: 'Europlatform',
                orderProducer: 'DRS_EP',
                preferredDealer: shop.dealerText + (shop.dealerNip ? `;${shop.dealerNip}` : ''),
                // ordererPhone: null,
                companyNip: company.nip,
                companyName: company.name,
                companyBillingStreet: company.billingData.street,
                companyBillingBuilding: company.billingData.building,
                companyBillingFlat: company.billingData.local,
                companyBillingCity: company.billingData.city,
                companyBillingPostalCode: company.billingData.postalCode,
                companyBillingCountry: 'PL',
                merchantEmail: user.email,
                merchantName: user.name,
                merchantSurname: user.surname,
                locationIdCrm: shop.id,
                locationName: shop.full_name,
                locationStreet: shop.address.street,
                locationBuilding: shop.address.building,
                locationFlat: shop.address.local,
                locationCity: shop.address.city,
                locationPostalCode: shop.address.postal_code,
                locationCountry: 'PL',
                locationBackofficeId: shop.id
            }

            return this.$api.post('drs/api/proxy/orders', payload, {
                config: {
                    noCancel: true
                }
            })
        })
        await Promise.all(orderPromises)
    },

    async createOrdersForSelectedShops({ dispatch }, selectedShops) {
        try {
            // Do createOrder for every shop at once
            await Promise.all(selectedShops.map(shop => dispatch('createOrder', shop)))
        } catch (err) {
            console.error('Error creating orders for selected shops:', err)
            throw err
        }
    },

    setConfigMode({ commit }, configMode) {
        commit('SET_CONFIG_MODE', configMode)
        commit('SET_CART', null)
    },

    async fetchOrdersByLocationIds({ commit }, locationIds) {
        try {
            return await Promise.all(locationIds.map(id => {
                // Since API doesn't support right endpoint, temporary we have to use `by-location-id-crm`
                // instead of `by-bo-location-id`
                return store.$api.get(`drs/api/proxy/orders/by-location-id-crm/${id}`, {
                    config: {
                        noCancel: true
                    }
                })
            }))
        } catch (error) {
            console.error('Error fetching orders by location IDs:', error)
        }
    },

    async loadTariffConfigFromStorage({ commit }) {
        // Get selectedTariff from localStorage and remove items from localStorage
        let tariffConfig = await store.$storage.getItem('tariffConfig')

        if (!tariffConfig) {
            return
        }

        tariffConfig = JSON.parse(tariffConfig)

        commit('UPDATE_TARIFFS', tariffConfig.selectedTariff)
        commit('SET_SELECTED_PAYMENT_TYPE', tariffConfig.selectedPaymentType)
        commit('SET_INVOICE_EMAIL', tariffConfig.invoiceEmail)
        commit('SET_TARIFFS', tariffConfig.tariffs)
    },

    resetTariff({ commit }) {
        commit('UPDATE_TARIFFS', null)
        commit('SET_SELECTED_PAYMENT_TYPE', 'automatic')
        commit('SET_INVOICE_EMAIL', '')
    },

    updateSelectedTariff({ commit }, { tariff, locations }) {
        const selectedLocations = locations.map(({
            location,
            additionalDevices,
            deviceTypeSubscriptions
        }) => ({
            ...location,
            selected: true,
            additionalDevices,
            optionals: tariff.optionals.list.map(optional => {
                const amount = deviceTypeSubscriptions.find(
                    ({ code }) => code === optional.code
                )?.amount || 0

                const baseAmount = optional.code === 'ecr' ? 1 : 0

                return {
                    ...optional,
                    qty: Math.max(amount - baseAmount, 0)
                }
            })
        }))

        commit('UPDATE_TARIFFS', {
            ...cloneDumb(tariff),
            locations: selectedLocations
        })
    },

    postSubscription({ getters, rootGetters }) {
        const payload = {
            nip: rootGetters['drs/nip'],
            payment_type: getters.selectedPaymentType,
            return_url: window.location.origin + router.resolve({
                name: 'drs',
                query: { paymentReturn: 'true' }
            }).href,
            payment_description: 'Płatność za subskrypcję w serwisie Kaucyjni.pl',
            locations: getters.cartLocations,
            user: {
                email: rootGetters.getUser.email
            }
        }

        try {
            return this.$api.post('drs/api/proxy/zor/v2/subscriptions', payload, {
                params: {
                    service_code: 'KEC'
                }
            })
        } catch (error) {
            return error
        }
    }
}

const getters = {
    tariffs: (state) => {
        const toMajorUnits = value => (value ? value / 100 : 0)
        const getMaxDeviceAmount = (deviceTypes, field) => {
            if (!Array.isArray(deviceTypes)) {
                return 0
            }

            return deviceTypes.reduce((maxPrice, deviceType) => {
                const deviceAmount = Number(deviceType?.[field] / 100) || 0
                return Math.max(maxPrice, deviceAmount)
            }, 0)
        }

        return state.tariffs.map(tariff => {
            const devices_price = getMaxDeviceAmount(tariff.device_types, 'price')
            const devices_discounted_price = getMaxDeviceAmount(tariff.device_types, 'discounted_price')

            return {
                ...tariff,
                ...TARIFF,
                price: toMajorUnits(tariff.price),
                discounted_price: tariff.discounted_price ? tariff.discounted_price / 100 : null,
                devices_price,
                devices_discounted_price
            }
        })
    },
    tariffGroups: (state, getters) => {
        // Group tariffs by tariff_group.id, if tariff_group is null, group by tariff.name
        return getters.tariffs.reduce((groups, tariff) => {
            const groupId = tariff?.tariff_group?.id || null
            const groupName = tariff?.tariff_group?.name || tariff.name

            let group = groups.find(item => item.id === groupId)
            if (!group) {
                group = {
                    id: groupId,
                    name: groupName,
                    tariffs: []
                }
                groups.push(group)
            }

            group.tariffs.push(tariff)
            return groups
        }, [])
    },
    selectedTariff: (state) => state.selectedTariff,
    cartLocations: (state) => {
        const tariff = state.selectedTariff

        if (!tariff) {
            return []
        }

        return (tariff.locations || [])
            .filter(location => location.selected)
            .map(location => ({
                bo_location_id: location.id,
                tariff_id: tariff.id,
                device_type_subscriptions: tariff.device_types.map(device => {
                    const optional = tariff.optionals.list.find(({ code }) => code === device.code)

                    return {
                        code: device.code,
                        amount: location.additionalDevices && optional
                            ? optional.maxQty
                            : device.free_device_limit || 0
                    }
                })
            }))
    },
    cart: state => state.cart,
    isCartLoading: state => state.isCartLoading,
    getDevices: (state) => state.devices,
    configMode: state => state.configMode,
    configTariffTitle: state => {
        switch (state.configMode) {
        case CONFIG_MODES.EDIT:
            return 'Edycja pakietu'
        case CONFIG_MODES.MAPPING:
            return 'Aktualizacja pakietu System Kaucyjny'
        default:
            return 'Konfiguracja pakietu System Kaucyjny'
        }
    },
    invoiceEmail: (state) => state.invoiceEmail,
    selectedPaymentType: (state) => state.selectedPaymentType,
    availableShops: (state, getters, rootState, rootGetters) => selectedShopId => {
        if (state.configMode === CONFIG_MODES.EDIT) {
            const shopsWithActiveService = rootGetters['drs/shopsWithActiveService']

            if (selectedShopId) {
                return shopsWithActiveService.filter(({ id }) => id === selectedShopId)
            } else if (state.selectedTariff?.locations) {
                const selectedLocationIds = state.selectedTariff.locations
                    .filter(({ selected }) => selected)
                    .map(({ id }) => id)

                return shopsWithActiveService.filter(({ id }) => selectedLocationIds.includes(id))
            }

            return shopsWithActiveService
        } else if (state.configMode === CONFIG_MODES.DEALER_EDIT) {
            return rootGetters['drs/getLocations'].filter(({ id }) => id === selectedShopId)
        }

        return rootGetters['drs/shopsWithoutActiveSubscription']
    },
    tariffsCopy: (state) => state.tariffsCopy
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}